<div class="zn">

  <!-- ── Strain budget: the load-bearing numbers, pinned above every view ── -->
  @if (strains?.length) {
    <div class="zn-budget" [class.is-multi]="multiStrain">
      @for (strain of strains; track strain.strainId) {
        <!-- Not a <button>: it contains the editable target input, and nesting a
             form field inside a button breaks focus in Safari and Firefox. -->
        <div class="zn-chip"
          [class.is-active]="strain.strainId === activeStrainId"
          [class.is-solo]="!multiStrain"
          [attr.data-state]="strainState(strain)"
          [attr.role]="multiStrain ? 'button' : null"
          [attr.tabindex]="multiStrain ? 0 : null"
          [attr.aria-pressed]="multiStrain ? (strain.strainId === activeStrainId) : null"
          (click)="setActiveStrain(strain)"
          (keydown.enter)="setActiveStrain(strain)"
          (keydown.space)="setActiveStrain(strain)">

          <div class="zn-chip__head">
            <span class="zn-chip__name">{{strain.strainName}}</span>
            @if (multiStrain && strain.strainId === activeStrainId) {
              <span class="zn-chip__active">Active</span>
            }
          </div>

          <div class="zn-chip__nums">
            <span class="zn-chip__placed">{{placedFor(strain.strainId)}}</span>
            <span class="zn-chip__of">of</span>
            <!-- The phase target is editable here so a blank target doesn't force
                 cancelling out of the dialog and losing the allocation. -->
            <input #target type="number" class="zn-chip__target" min="0"
              [value]="strain.count || 0" (input)="setTarget(strain, target)"
              (click)="$event.stopPropagation()"
              [attr.aria-label]="strain.strainName + ' target plant count'">
            <span class="zn-chip__unit">plants</span>
          </div>

          <div class="zn-meter">
            <div class="zn-meter__fill" [style.width.%]="progressFor(strain)"></div>
          </div>

          <div class="zn-chip__foot">
            @switch (strainState(strain)) {
              @case ('none') {
                <span class="zn-chip__note">Set a target to track placement</span>
              }
              @case ('over') {
                <span class="zn-chip__note is-alarm">
                  <mat-icon>error_outline</mat-icon>
                  {{placedFor(strain.strainId) - targetFor(strain)}} over target
                </span>
              }
              @case ('exact') {
                <span class="zn-chip__note is-good">
                  <mat-icon>check_circle</mat-icon> Fully placed
                </span>
              }
              @default {
                <span class="zn-chip__note">{{remainingFor(strain.strainId)}} left to place</span>
              }
            }
          </div>
        </div>
      }

      @if (activeStrain && remainingFor(activeStrainId) > 0 && totalZonesAssigned > 0) {
        <button type="button" class="zn-distribute" (click)="distributeRemaining()">
          <mat-icon>auto_awesome_motion</mat-icon>
          <span>Distribute {{remainingFor(activeStrainId)}} remaining</span>
        </button>
      }
    </div>
  }

  <!-- ── Toolbar ── -->
  @if (hasZones) {
    <div class="zn-toolbar">
      <div class="zn-searchwrap">
        <mat-icon>search</mat-icon>
        <input type="text" class="zn-search" placeholder="Search zones"
          [(ngModel)]="search" [ngModelOptions]="{standalone: true}" aria-label="Search zones">
        @if (search) {
          <button type="button" class="zn-search__clear" (click)="search = ''" aria-label="Clear search">
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>

      <div class="zn-toolbar__right">
        @if (totalZonesAssigned > 0) {
          <button type="button" class="zn-ghostbtn" (click)="clearAll()">Clear all</button>
        }
        @if (canShowMap) {
          <div class="zn-viewtoggle">
            <button type="button" class="zn-viewtoggle__btn" [class.is-active]="viewMode === 'list'"
              (click)="setView('list')">
              <mat-icon>view_list</mat-icon> List
            </button>
            <button type="button" class="zn-viewtoggle__btn" [class.is-active]="viewMode === 'map'"
              (click)="setView('map')">
              <mat-icon>map</mat-icon> Map
            </button>
          </div>
        }
      </div>
    </div>
  }

  <!-- ── Body ── -->
  <div class="zn-body">

    @if (loading) {
      <div class="zn-empty"><mat-spinner diameter="26" strokeWidth="3"></mat-spinner></div>
    }

    @if (!loading && !strains?.length) {
      <div class="zn-empty">
        <mat-icon>local_florist</mat-icon>
        <p class="zn-empty__title">No strains on this phase</p>
        <p class="zn-empty__hint">Add strains to the phase before assigning zones.</p>
      </div>
    }

    @if (!loading && strains?.length && !hasZones) {
      <div class="zn-empty">
        <mat-icon>grid_off</mat-icon>
        <p class="zn-empty__title">No zones set up yet</p>
        <p class="zn-empty__hint">Create zones under Grow Settings to assign plants to them.</p>
      </div>
    }

    <!-- List view. Kept in the DOM while the map shows so scroll position and
         any in-progress edit survive a round trip through the map. -->
    @if (!loading && hasZones) {
      <div class="zn-list" [hidden]="viewMode === 'map'">
        @for (group of visibleGroups; track group.key) {
          <section class="zn-group">

            <header class="zn-group__head" (click)="toggleGroup(group)">
              <span class="zn-check"
                [class.is-on]="groupAllAssigned(group)"
                [class.is-partial]="groupSomeAssigned(group)">
                <mat-icon>{{groupAllAssigned(group) ? 'check' : 'remove'}}</mat-icon>
              </span>
              <span class="zn-group__label">{{group.label}}</span>
              <span class="zn-group__stats">
                <span class="zn-group__zones">{{groupAssignedCount(group)}} of {{group.leaves.length}} zones</span>
                <span class="zn-group__dot">·</span>
                <span class="zn-group__cap">{{groupPlaced(group)}} / {{groupCapacity(group)}} capacity</span>
              </span>
            </header>

            @for (row of group.leaves; track row.id) {
              <div class="zn-row" [class.is-assigned]="row.assigned" [attr.data-state]="rowState(row)">

                <!-- Whole strip toggles assignment; the controls stop propagation. -->
                <div class="zn-row__main" (click)="toggleRow(row)">
                  <span class="zn-check" [class.is-on]="row.assigned">
                    <mat-icon>check</mat-icon>
                  </span>

                  <span class="zn-row__name">{{row.name}}</span>

                  <span class="zn-row__cap">
                    @if (row.assigned) {
                      <span class="zn-row__used">{{rowTotal(row)}}</span><span class="zn-row__slash">/</span><span class="zn-row__total">{{row.capacity}}</span>
                    } @else {
                      <span class="zn-row__total">{{row.capacity}} spaces</span>
                    }
                  </span>

                  <span class="zn-meter zn-meter--row">
                    <span class="zn-meter__fill" [style.width.%]="rowProgress(row)"></span>
                  </span>

                  <span class="zn-row__tag">
                    @switch (rowState(row)) {
                      @case ('full')  { <span class="zn-tag is-full">Full</span> }
                      @case ('over')  { <span class="zn-tag is-over">Over</span> }
                      @case ('empty') {
                        @if (row.assigned) { <span class="zn-tag is-empty">No plants</span> }
                      }
                    }
                  </span>
                </div>

                <!-- Allocation controls appear only once the zone is in use. -->
                @if (row.assigned) {
                  <div class="zn-allocs" (click)="$event.stopPropagation()">
                    @for (alloc of row.allocations; track $index) {
                      <div class="zn-alloc">
                        @if (multiStrain) {
                          <!-- Only strains not already in this zone, so one strain
                               can't be allocated twice and double-count. -->
                          <select class="zn-alloc__strain" [value]="alloc.strainId"
                            (change)="setAllocStrain(row, alloc, $any($event.target).value)"
                            aria-label="Strain">
                            @for (s of strainOptionsFor(row, alloc); track s.strainId) {
                              <option [value]="s.strainId">{{s.strainName}}</option>
                            }
                          </select>
                        } @else {
                          <span class="zn-alloc__solo">{{strainName(alloc.strainId)}}</span>
                        }

                        <input #count type="number" class="zn-alloc__count" min="0" [max]="row.capacity"
                          [value]="alloc.count" (input)="setCount(row, alloc, count)"
                          aria-label="Plants in this zone">
                        <span class="zn-alloc__unit">plants</span>

                        @if (multiStrain && availableStrainsFor(row).length > 0) {
                          <button type="button" class="zn-iconbtn" (click)="addAllocation(row)" title="Add another strain">
                            <mat-icon>add</mat-icon>
                          </button>
                        }
                        @if (row.allocations.length > 1) {
                          <button type="button" class="zn-iconbtn" (click)="removeAllocation(row, alloc)" title="Remove strain">
                            <mat-icon>close</mat-icon>
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </section>
        }

        @if (visibleGroups.length === 0) {
          <div class="zn-empty">
            <mat-icon>search_off</mat-icon>
            <p class="zn-empty__title">No zones match “{{search}}”</p>
          </div>
        }
      </div>

      @if (viewMode === 'map') {
        <div class="zn-map">
          <app-zone-map-viewer
            [floorPlan]="activeFloorPlan"
            [zones]="zonesList"
            [interactive]="true"
            [selectedIds]="mapSelectedIds"
            [fillStates]="mapFillStates"
            [badges]="mapBadges"
            (zoneClick)="onZoneTap($event)">
          </app-zone-map-viewer>

          <div class="zn-map__legend">
            <span class="zn-map__hint"><mat-icon>touch_app</mat-icon> Tap a zone to fill it from the active strain.</span>
            <span class="zn-legend"><i class="zn-swatch" data-state="empty"></i> Unused</span>
            <span class="zn-legend"><i class="zn-swatch" data-state="partial"></i> Partly filled</span>
            <span class="zn-legend"><i class="zn-swatch" data-state="full"></i> Full</span>
            <span class="zn-legend"><i class="zn-swatch" data-state="over"></i> Over</span>
          </div>
        </div>
      }
    }
  </div>
</div>
